import React, { useEffect, useState } from 'react';
import MyTable from '../components/Table';
import { doc, setDoc, deleteDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useLocation, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import NotFound from './NotFound';
import { useAtom } from 'jotai';
import { userAtom } from '../atom';
import { getFormattedDate, updateFirebaseGlobalDataFetchingDate } from '../components/helperfunction';

const AddingData = ({ isAdmin }) => {
  const user = useAtom(userAtom)?.[0];
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const cardData = location.state || {
    id: getFormattedDate(),
    pastPapers: [], CO_display: '', title: '', books: [],
    others: [], quiz: [], description: '', CO_link: '',
    labTasks: [], assignments: [],
  };

  const [form, setForm] = useState({
    title: cardData.title || '',
    description: cardData.description || '',
    coDisplay: cardData.CO_display || '',
    coLink: cardData.CO_link || '',
  });

  const [quizTableData, setQuizTableData] = useState(cardData.quiz);
  const [pastPapersTableData, setPastPapersTableData] = useState(cardData.pastPapers);
  const [booksTableData, setBooksTableData] = useState(cardData.books);
  const [assignmentTableData, setAssignmentTableData] = useState(cardData.assignments);
  const [othersTableData, setOthersTableData] = useState(cardData.others);
  const [labTasksData, setLabTasksTableData] = useState(cardData.labTasks);

  useEffect(() => {
    document.title = 'Add Data — SitsCom';
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      message.warning('Title and description are required');
      return;
    }
    setLoading(true);
    const docRef = doc(db, 'data', cardData.id);
    const data = {
      id: cardData.id,
      title: form.title,
      description: form.description,
      CO_display: form.coDisplay,
      CO_link: form.coLink,
      quiz: quizTableData,
      pastPapers: pastPapersTableData,
      books: booksTableData,
      assignments: assignmentTableData,
      others: othersTableData,
      labTasks: labTasksData,
    };
    await setDoc(docRef, { data });
    message.success('Data uploaded/updated');
    setLoading(false);
    await updateFirebaseGlobalDataFetchingDate();
  };

  const deleteDocument = async () => {
    if (user?.email !== 'muhammadsaboor119@gmail.com') {
      message.error("You don't have permission to delete");
      return;
    }
    setLoading(true);
    const docRef = doc(db, 'data', cardData.id);
    const snap = await getDoc(docRef);
    if (!snap.exists()) {
      message.error('Document does not exist');
    } else {
      await deleteDoc(docRef);
      message.success('Document deleted');
      navigate('/home');
    }
    setLoading(false);
    await updateFirebaseGlobalDataFetchingDate();
  };

  if (!isAdmin) return <NotFound />;

  return (
    <div className="add-data-page page-container">
      <div className="add-data-header fade-in-up" style={{ opacity: 0, animationDelay: '0.05s' }}>
        <div>
          <h1>{location.state ? 'Edit Subject' : 'Add New Subject'}</h1>
          <p>Manage course materials and resources</p>
        </div>
        <button className="btn btn-ghost" onClick={() => navigate('/home')}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back
        </button>
      </div>

      <form onSubmit={onSubmit} className="add-data-form fade-in-up" style={{ opacity: 0, animationDelay: '0.12s' }}>
        <div className="add-data-card">
          <h3 className="add-data-section-title">Subject Details</h3>
          <div className="add-data-fields">
            <div className="form-group">
              <label>Title <span className="required">*</span></label>
              <input
                type="text"
                name="title"
                className="input"
                value={form.title}
                onChange={handleChange}
                placeholder="e.g. Data Structures & Algorithms"
                required
              />
            </div>
            <div className="form-group">
              <label>Description <span className="required">*</span></label>
              <textarea
                name="description"
                className="input"
                value={form.description}
                onChange={handleChange}
                placeholder="Brief description of the course"
                required
                rows={3}
                maxLength={165}
                style={{ resize: 'vertical' }}
              />
              <span className="char-count">{form.description.length}/165</span>
            </div>
            <div className="form-row-2col">
              <div className="form-group">
                <label>CO Display</label>
                <input
                  type="text"
                  name="coDisplay"
                  className="input"
                  value={form.coDisplay}
                  onChange={handleChange}
                  placeholder="Course outline display text"
                />
              </div>
              <div className="form-group">
                <label>CO Link</label>
                <input
                  type="text"
                  name="coLink"
                  className="input"
                  value={form.coLink}
                  onChange={handleChange}
                  placeholder="https://..."
                />
              </div>
            </div>
          </div>
        </div>

        <div className="add-data-card">
          <h3 className="add-data-section-title">Resources</h3>
          <MyTable title="Quiz" defaultData={cardData.quiz} setTableData={setQuizTableData} />
          <MyTable title="Past Papers" defaultData={cardData.pastPapers} setTableData={setPastPapersTableData} />
          <MyTable title="Books" defaultData={cardData.books} setTableData={setBooksTableData} />
          <MyTable title="Assignments" defaultData={cardData.assignments} setTableData={setAssignmentTableData} />
          <MyTable title="Lab Tasks" defaultData={cardData.labTasks} setTableData={setLabTasksTableData} />
          <MyTable title="Others" defaultData={cardData.others} setTableData={setOthersTableData} />
        </div>

        <div className="add-data-actions">
          <button
            type="submit"
            className="btn btn-primary btn-lg"
            disabled={loading}
            style={{ flex: 1 }}
          >
            {loading ? 'Saving...' : (location.state ? 'Update Subject' : 'Create Subject')}
          </button>
          {location.state && (
            <>
              {!deleteConfirm ? (
                <button
                  type="button"
                  className="btn btn-danger btn-lg"
                  onClick={() => setDeleteConfirm(true)}
                >
                  Delete
                </button>
              ) : (
                <div className="delete-confirm">
                  <span>Are you sure?</span>
                  <button type="button" className="btn btn-danger btn-sm" onClick={deleteDocument} disabled={loading}>
                    {loading ? 'Deleting...' : 'Yes, delete'}
                  </button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setDeleteConfirm(false)}>
                    Cancel
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </form>
    </div>
  );
};

export default AddingData;
